import { Box } from "@mui/material";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";

import { TASK_CARD_FIXED_WIDTH_PX, TaskCard } from "../../atoms/TaskCard/TaskCard";
import { TaskContent } from "../../molecules/TaskContent/TaskContent";
import { type CarouselTaskItem } from "./taskUtils";

export interface TaskDashboardDesktopCarouselProps {
  allTasks: CarouselTaskItem[];
  selectedTask: CarouselTaskItem | null | undefined;
  onSelectTask: (task: CarouselTaskItem) => void;
  onCompleteTask: (task: CarouselTaskItem) => (event: React.MouseEvent) => void;
  isTaskCompleting: (taskId?: string) => boolean;
}

/** Space between task cards (padding-right on each virtual column). */
const CARD_HORIZONTAL_GAP_PX = 24;

export const TaskDashboardDesktopCarousel = ({
  allTasks,
  selectedTask,
  onSelectTask,
  onCompleteTask,
  isTaskCompleting,
}: TaskDashboardDesktopCarouselProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualColumnWidthPx = TASK_CARD_FIXED_WIDTH_PX + CARD_HORIZONTAL_GAP_PX;

  const virtualizer = useVirtualizer({
    count: allTasks.length,
    estimateSize: () => virtualColumnWidthPx,
    getScrollElement: () => scrollRef.current,
    horizontal: true,
    // Fixed column stride: DOM measurement can inflate (e.g. scale transforms) and collapse gaps / overlap items.
    overscan: 6,
  });

  const virtualizerRef = useRef(virtualizer);
  virtualizerRef.current = virtualizer;

  useEffect(() => {
    if (!selectedTask || allTasks.length === 0) {
      return;
    }
    const idx = allTasks.findIndex((t) => t.id === selectedTask.id);
    if (idx < 0) {
      return;
    }
    virtualizerRef.current.scrollToIndex(idx, { align: "auto" });
  }, [allTasks, selectedTask]);

  return (
    <Box
      sx={{
        maxWidth: "100%",
        minWidth: 0,
        pb: 2,
        width: "100%",
      }}
    >
      <Box
        ref={scrollRef}
        sx={{
          maxWidth: "100%",
          minHeight: 320,
          minWidth: 0,
          overflowX: "auto",
          overflowY: "hidden",
          scrollBehavior: "smooth",
          // Reserve space for the horizontal scrollbar where supported (avoids clipping / layout jump).
          scrollbarGutter: "stable",
          width: "100%",
        }}
      >
        <div
          style={{
            height: "100%",
            minHeight: 300,
            position: "relative",
            width: virtualizer.getTotalSize(),
          }}
        >
          {virtualizer.getVirtualItems().map((virtualCol) => {
            const task = allTasks[virtualCol.index];
            return (
              <div
                key={task.id}
                data-index={virtualCol.index}
                style={{
                  height: "100%",
                  left: 0,
                  position: "absolute",
                  top: 0,
                  transform: `translateX(${virtualCol.start}px)`,
                  width: virtualCol.size,
                }}
              >
                <Box
                  sx={{
                    boxSizing: "border-box",
                    height: "100%",
                    pr: `${CARD_HORIZONTAL_GAP_PX}px`,
                    py: 1,
                    width: "100%",
                  }}
                >
                  <TaskCard
                    isCompleting={isTaskCompleting(task.id)}
                    isSelected={task.id === selectedTask?.id}
                    layout="fluid"
                    onClick={() => onSelectTask(task)}
                    onComplete={onCompleteTask(task)}
                  >
                    <TaskContent task={task} />
                  </TaskCard>
                </Box>
              </div>
            );
          })}
        </div>
      </Box>
    </Box>
  );
};
